import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  startAfter,
  limit as fbLimit,
  writeBatch,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  getCountFromServer
} from 'firebase/firestore';
import { db } from 'config/firebase';
import logger from 'utils/logger';

// ============================================
// USERS SERVICE
// ============================================
export const usersService = {
  /**
   * Create or update a user profile document on signup/login
   */
  async createUserProfile(user) {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        // Update last login
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          displayName: user.displayName || userSnap.data().displayName,
          email: user.email
        });
        return { id: user.uid, ...userSnap.data(), existing: true };
      }

      // Create new user profile
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        phone: '',
        address: '',
        city: '',
        state: '',
        profilePhoto: user.photoURL || '',
        role: 'customer',
        preferences: {},
        orderCount: 0,
        totalSpent: 0,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };

      await setDoc(userRef, userData);
      return { id: user.uid, ...userData, existing: false };
    } catch (error) {
      logger.error('Error creating user profile:', error);
      throw error;
    }
  },

  /**
   * Get user profile by UID
   */
  async getUserProfile(uid) {
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      }
      return null;
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   */
  async updateUserProfile(uid, updates) {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      logger.error('Error updating user profile:', error);
      throw error;
    }
  },

  /**
   * Get all users (admin only)
   */
  async getAllUsers() {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('Error fetching all users:', error);
      throw error;
    }
  },

};

// Order Status Constants
export const ORDER_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  ON_HOLD: 'on-hold',
  PROCESSING: 'processing',
  CANCELLED: 'cancelled'
};

// ---------------------------------------------------------------------------
// Order status transition map — SINGLE SOURCE OF TRUTH (STORY-01.3.04 AC-2).
//
// Keyed by current payment_status → the statuses it may move to. Terminal
// statuses (completed, cancelled, failed) have no outbound transitions —
// refunds / re-opens are handled by dedicated flows, not the status select.
//
// Consumed by:
//   - OrderDetail.jsx status select  → offers only allowed destinations
//   - ordersService.updateOrder       → rejects an illegal single-order jump
//   - ordersService.bulkUpdateStatus  → validates the target is a known status
//
// NOTE on bulk: the bulk action bar (FEAT-01.2) is an explicit operator
// fulfilment override — e.g. "mark these completed" may skip pending→processing.
// So bulk deliberately does NOT impose the step-by-step rules the single-order
// select does; it draws on this same module only to reject unknown statuses.
// See ADR-0024.
// ---------------------------------------------------------------------------
export const ORDER_STATUS_TRANSITIONS = {
  pending:    ['processing', 'on-hold', 'cancelled', 'failed'],
  processing: ['completed', 'on-hold', 'cancelled'],
  'on-hold':  ['processing', 'cancelled'],
  completed:  [],
  cancelled:  [],
  failed:     [],
};

/** Destinations allowed from `status` (empty array for unknown / terminal). */
export const getAllowedTransitions = (status) =>
  ORDER_STATUS_TRANSITIONS[String(status || '').toLowerCase()] ?? [];

/** True when a known status. */
export const isKnownStatus = (status) =>
  Object.prototype.hasOwnProperty.call(
    ORDER_STATUS_TRANSITIONS, String(status || '').toLowerCase()
  );

/** True when moving from→to is an allowed transition. from===to is a no-op (allowed). */
export const isValidTransition = (from, to) => {
  const f = String(from || '').toLowerCase();
  const t = String(to || '').toLowerCase();
  if (f === t) return true;
  return getAllowedTransitions(f).includes(t);
};

/**
 * Throw a descriptive error when from→to is not an allowed transition. Used by
 * the single-order service write path so an illegal jump forced past the UI
 * (e.g. a direct API/script call) is still rejected at the service layer.
 */
export const assertValidTransition = (from, to) => {
  if (!isValidTransition(from, to)) {
    const allowed = getAllowedTransitions(from);
    const hint = allowed.length ? allowed.join(', ') : 'none (terminal status)';
    throw new Error(
      `Invalid order status transition: "${from}" → "${to}". Allowed from "${from}": ${hint}.`
    );
  }
};

// Orders Collection
export const ordersService = {
  async createOrder(orderData) {
    const ordersRef = collection(db, 'orders');
    const orderNumber = `CL-${Date.now()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

    const orderDoc = {
      order_number: orderNumber,
      customer_email: orderData.email,
      customer_name: orderData.fullName,
      customer_phone: orderData.phone,
      shipping_address: orderData.address,
      shipping_city: orderData.city,
      shipping_state: orderData.state,
      order_notes: orderData.notes || '',
      order_items: orderData.cartItems,
      subtotal: orderData.subtotal,
      shipping_cost: orderData.shippingCost,
      total: orderData.total,
      payment_status: ORDER_STATUS.PENDING,
      payment_reference: orderData.paymentReference || '',
      // Include user ID if authenticated
      user_id: orderData.userId || null,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    };

    const docRef = await addDoc(ordersRef, orderDoc);
    return {
      success: true,
      orderId: docRef.id,
      orderNumber,
      paymentReference: orderData.paymentReference || `ref_${orderNumber}_${Date.now()}`
    };
  },

  async getOrderByNumber(orderNumber) {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, where('order_number', '==', orderNumber));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  },

  async getOrderById(orderId) {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        return {
          id: orderSnap.id,
          ...orderSnap.data()
        };
      }
      return null;
    } catch (error) {
      logger.error('Error fetching order by ID:', error);
      throw error;
    }
  },

  async updateOrderStatus(orderId, status, transactionId = null) {
    const orderRef = doc(db, 'orders', orderId);
    const updateData = {
      payment_status: status,
      updated_at: serverTimestamp()
    };

    if (transactionId) {
      updateData.transaction_id = transactionId;
    }

    await updateDoc(orderRef, updateData);
  },

  async getUserOrders(userEmail, userId = null) {
    const ordersRef = collection(db, 'orders');
    // If userId is provided, query by userId (more efficient)
    // Otherwise, fall back to email query
    let q;
    if (userId) {
      q = query(
        ordersRef,
        where('user_id', '==', userId),
        orderBy('created_at', 'desc')
      );
    } else {
      q = query(
        ordersRef,
        where('customer_email', '==', userEmail),
        orderBy('created_at', 'desc')
      );
    }
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  // ========== ADMIN METHODS ==========

  /**
   * Get all orders with optional status filter (admin only)
   */
  async getAllOrders(statusFilter = null, limit = 100) {
    try {
      const ordersRef = collection(db, 'orders');
      let q;

      if (statusFilter) {
        q = query(
          ordersRef,
          where('payment_status', '==', statusFilter),
          orderBy('created_at', 'desc')
        );
      } else {
        q = query(
          ordersRef,
          orderBy('created_at', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return orders.slice(0, limit);
    } catch (error) {
      logger.error('Error fetching all orders:', error);
      throw error;
    }
  },

  /**
   * Get one page of orders for the admin Orders list (cursor pagination).
   *
   * Story: STORY-01.2.03 — server-cursor pagination.
   *
   * Forward-only Firestore cursor (`startAfter`) at the requested pageSize.
   * Returns { orders, nextCursor, hasMore }. The caller persists `nextCursor`
   * to fetch the next page; pass null to start from the beginning.
   *
   * @param {Object}  opts
   * @param {string?} opts.statusFilter — optional payment_status filter
   * @param {number}  opts.pageSize     — default 25
   * @param {Object?} opts.cursor       — last DocumentSnapshot of previous page
   * @param {string}  opts.sortKey      — 'created_at' (default) | 'total'
   * @param {string}  opts.direction    — 'asc' | 'desc' (default)
   * @returns {Promise<{orders, nextCursor, hasMore}>}
   */
  async getOrdersPage({
    statusFilter = null,
    pageSize = 25,
    cursor = null,
    sortKey = 'created_at',
    direction = 'desc',
  } = {}) {
    try {
      const ordersRef = collection(db, 'orders');
      const constraints = [];
      if (statusFilter) constraints.push(where('payment_status', '==', statusFilter));
      constraints.push(orderBy(sortKey, direction));
      if (cursor) constraints.push(startAfter(cursor));
      // Fetch pageSize+1 to detect hasMore without a second round-trip.
      constraints.push(fbLimit(pageSize + 1));

      const q = query(ordersRef, ...constraints);
      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      const hasMore = docs.length > pageSize;
      const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

      return {
        orders: pageDocs.map((d) => ({ id: d.id, ...d.data() })),
        nextCursor: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
        hasMore,
      };
    } catch (error) {
      logger.error('Error fetching orders page:', error);
      throw error;
    }
  },

  /**
   * Bulk-update many orders to the same target status (STORY-01.2.04).
   *
   * Writes a single Firestore batch (chunks of 500 to stay under the API
   * limit). Each doc gets `payment_status` + `updated_at`. Returns
   * `{ success, count }`.
   *
   * @param {string[]} orderIds  — Firestore doc IDs (not order_number)
   * @param {string}   status    — target payment_status
   */
  async bulkUpdateStatus(orderIds, status) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return { success: true, count: 0 };
    }
    // Draw on the single-source transition module (STORY-01.3.04 AC-2) to
    // reject an unknown target status. Bulk is an explicit operator fulfilment
    // override, so it does NOT impose the step-by-step single-order rules
    // (e.g. it may mark pending orders completed) — see ADR-0024.
    const target = String(status || '').toLowerCase();
    if (!isKnownStatus(target)) {
      throw new Error(
        `Bulk status change rejected — "${status}" is not a known order status.`
      );
    }
    const CHUNK = 500;
    let totalWritten = 0;
    try {
      for (let i = 0; i < orderIds.length; i += CHUNK) {
        const chunk = orderIds.slice(i, i + CHUNK);
        const batch = writeBatch(db);
        for (const id of chunk) {
          batch.update(doc(db, 'orders', id), {
            payment_status: target,
            updated_at: serverTimestamp(),
          });
        }
        await batch.commit();
        totalWritten += chunk.length;
      }
      return { success: true, count: totalWritten };
    } catch (error) {
      logger.error('Error in bulkUpdateStatus:', error);
      throw error;
    }
  },

  /**
   * Mark a set of orders as printed (STORY-01.5.02).
   *
   * Writes `printed_at: serverTimestamp()` to each selected order. Used by
   * the bulk action bar's "Mark printed" button so the operator can track
   * which orders have been printed for courier handoff. Already-printed
   * orders get the timestamp refreshed — re-marking is a no-op semantically.
   *
   * Returns { success, count }. Chunked at 500 per Firestore batch limit.
   */
  async markAsPrinted(orderIds) {
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return { success: true, count: 0 };
    }
    const CHUNK = 500;
    let totalWritten = 0;
    try {
      for (let i = 0; i < orderIds.length; i += CHUNK) {
        const chunk = orderIds.slice(i, i + CHUNK);
        const batch = writeBatch(db);
        for (const id of chunk) {
          batch.update(doc(db, 'orders', id), {
            printed_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });
        }
        await batch.commit();
        totalWritten += chunk.length;
      }
      return { success: true, count: totalWritten };
    } catch (error) {
      logger.error('Error in markAsPrinted:', error);
      throw error;
    }
  },

  /**
   * Subscribe to a live count of orders in a given status (STORY-01.2.05).
   *
   * Returns the unsubscribe function. Used by the AdminLayout sidebar to
   * keep the "Orders" badge live without re-fetching on every page nav.
   *
   * @param {string}   status   — payment_status to watch (typically 'pending')
   * @param {Function} onChange — called with the count whenever it changes
   */
  subscribeToStatusCount(status, onChange) {
    const q = query(collection(db, 'orders'), where('payment_status', '==', status));
    return onSnapshot(
      q,
      (snap) => onChange(snap.size),
      (err) => {
        logger.error('subscribeToStatusCount snapshot error:', err);
        // Fall back to 0 rather than leaving a stale value showing.
        onChange(0);
      }
    );
  },

  /**
   * Subscribe to a single order doc (STORY-01.3.03 AC-6).
   *
   * Used by the order-detail slide-over so the timeline + status reflect a
   * change made by another operator or a Cloud Function while the panel is
   * open — no manual refresh. Returns the unsubscribe function; the caller
   * MUST call it on close/unmount to avoid a leaked listener.
   *
   * @param {string}   orderId
   * @param {Function} onChange — called with { id, ...data } on each snapshot,
   *                              or null if the doc no longer exists.
   * @param {Function} [onError] — optional; snapshot errors are also logged.
   */
  subscribeToOrder(orderId, onChange, onError) {
    const ref = doc(db, 'orders', orderId);
    return onSnapshot(
      ref,
      (snap) => onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      (err) => {
        logger.error('subscribeToOrder snapshot error:', err);
        if (onError) onError(err);
      }
    );
  },

  /**
   * Update order (admin only).
   *
   * `internal_notes` MUST NOT be passed here — it lives in the admin-only
   * subcollection at orders/{orderId}/private/admin (firestore.rules forbids
   * the field on the parent doc). Use saveInternalNotes() instead. We strip
   * it defensively so a stale caller doesn't trigger a rule rejection.
   */
  async updateOrder(orderId, updates) {
    try {
      const safeUpdates = { ...(updates || {}) };
      if (Object.prototype.hasOwnProperty.call(safeUpdates, 'internal_notes')) {
        logger.warn('[ordersService.updateOrder] stripped internal_notes — use saveInternalNotes()');
        delete safeUpdates.internal_notes;
      }
      const orderRef = doc(db, 'orders', orderId);

      // Enforce the status-transition map at the service layer (STORY-01.3.04
      // AC-3). Reading the current doc first lets us (a) reject an illegal jump
      // forced past the UI and (b) hand the prior status to the audit wrapper
      // as `from`. Only runs when payment_status is actually being changed.
      let fromStatus = null;
      let toStatus = null;
      if (Object.prototype.hasOwnProperty.call(safeUpdates, 'payment_status')) {
        const snap = await getDoc(orderRef);
        if (!snap.exists()) {
          throw new Error(`Order ${orderId} not found.`);
        }
        fromStatus = String(snap.data().payment_status || 'pending').toLowerCase();
        toStatus = String(safeUpdates.payment_status || '').toLowerCase();
        assertValidTransition(fromStatus, toStatus);
        safeUpdates.payment_status = toStatus;
      }

      const updateData = {
        ...safeUpdates,
        updated_at: serverTimestamp()
      };

      await updateDoc(orderRef, updateData);
      // Surface from/to so the audit wrapper can log meta={from,to} (AC-4).
      return { success: true, ...(toStatus ? { from: fromStatus, to: toStatus } : {}) };
    } catch (error) {
      logger.error('Error updating order:', error);
      throw error;
    }
  },

  /**
   * Read the admin-only internal notes for an order.
   *
   * Lives at orders/{orderId}/private/admin so customer-owner reads of the
   * parent doc cannot surface it (firestore.rules: subcollection is admin
   * read/write only). Returns '' when unset — both for orders that never had
   * a note and for the post-migration zero-state.
   */
  async getInternalNotes(orderId) {
    try {
      const ref = doc(db, 'orders', orderId, 'private', 'admin');
      const snap = await getDoc(ref);
      if (!snap.exists()) return '';
      return snap.data().internal_notes || '';
    } catch (error) {
      logger.error('Error fetching internal notes:', error);
      throw error;
    }
  },

  /**
   * Persist the admin-only internal notes for an order.
   *
   * Writes orders/{orderId}/private/admin with merge semantics so future
   * admin-only fields can land in the same doc without overwriting the note.
   */
  async saveInternalNotes(orderId, text, adminUid = null) {
    try {
      const ref = doc(db, 'orders', orderId, 'private', 'admin');
      const payload = {
        internal_notes: text || '',
        updated_at: serverTimestamp(),
      };
      if (adminUid) payload.updated_by = adminUid;
      await setDoc(ref, payload, { merge: true });
      return { success: true };
    } catch (error) {
      logger.error('Error saving internal notes:', error);
      throw error;
    }
  },

  /**
   * Get order statistics (admin only)
   */
  async getOrderStats() {
    try {
      const ordersRef = collection(db, 'orders');
      const querySnapshot = await getDocs(ordersRef);

      const stats = {
        total: 0,
        pending: 0,
        completed: 0,
        failed: 0,
        onHold: 0,
        processing: 0,
        cancelled: 0,
        totalRevenue: 0,
        completedRevenue: 0
      };

      querySnapshot.docs.forEach(doc => {
        const order = doc.data();
        stats.total++;

        switch (order.payment_status) {
          case ORDER_STATUS.PENDING:
            stats.pending++;
            break;
          case ORDER_STATUS.COMPLETED:
            stats.completed++;
            stats.completedRevenue += order.total || 0;
            break;
          case ORDER_STATUS.FAILED:
            stats.failed++;
            break;
          case ORDER_STATUS.ON_HOLD:
            stats.onHold++;
            break;
          case ORDER_STATUS.PROCESSING:
            stats.processing++;
            break;
          case ORDER_STATUS.CANCELLED:
            stats.cancelled++;
            break;
        }

        stats.totalRevenue += order.total || 0;
      });

      return stats;
    } catch (error) {
      logger.error('Error fetching order stats:', error);
      throw error;
    }
  }
};

// ============================================
// PRODUCTS SERVICE
// ============================================
export const productsService = {
  /**
   * Get all active products with optional filtering
   */
  async getProducts(filters = {}) {
    try {
      const productsRef = collection(db, 'products');
      let q;

      // For admin: include inactive products
      if (filters.includeInactive) {
        q = query(productsRef);
      } else {
        q = query(productsRef, where('is_active', '==', true));
      }

      // Apply category filter
      if (filters.category && filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category));
      }

      // Apply featured filter
      if (filters.featured) {
        q = query(q, where('is_featured', '==', true));
      }

      // Note: Removed orderBy to avoid index requirement
      // We'll sort client-side instead while indexes are building

      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.data().product_id, // Use product_id for compatibility
        firestoreId: doc.id,
        ...doc.data()
      }));

      // Sort by created_at client-side
      return products.sort((a, b) => {
        const dateA = a.created_at?.toMillis?.() || 0;
        const dateB = b.created_at?.toMillis?.() || 0;
        return dateB - dateA; // Descending order (newest first)
      });
    } catch (error) {
      logger.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get single product by ID
   */
  async getProductById(productId) {
    try {
      const productRef = doc(db, 'products', `product_${productId}`);
      const productSnap = await getDoc(productRef);

      if (productSnap.exists()) {
        return {
          id: productSnap.data().product_id,
          firestoreId: productSnap.id,
          ...productSnap.data()
        };
      }
      return null;
    } catch (error) {
      logger.error('Error fetching product:', error);
      throw error;
    }
  },

  // ========== ADMIN METHODS ==========

  /**
   * Create new product (admin only)
   */
  async createProduct(productData, userId) {
    try {
      const productId = productData.product_id || Date.now();
      const productRef = doc(db, 'products', `product_${productId}`);
      const newProduct = {
        product_id: productId,
        title: productData.title,
        price: productData.price,
        image: productData.image || '',
        category: productData.category,
        description: productData.description,
        full_description: productData.full_description || productData.description,
        stock_quantity: productData.stock_quantity || 100,
        is_active: productData.is_active !== false,
        is_featured: productData.is_featured || false,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userId,
        updated_by: userId
      };

      await setDoc(productRef, newProduct);
      return {
        success: true,
        productId: productId,
        firestoreId: `product_${productId}`
      };
    } catch (error) {
      logger.error('Error creating product:', error);
      throw error;
    }
  },

  /**
   * Update product (admin only)
   */
  async updateProduct(productId, updates, userId) {
    try {
      const productRef = doc(db, 'products', `product_${productId}`);
      const updateData = {
        ...updates,
        updated_at: serverTimestamp(),
        updated_by: userId
      };

      await updateDoc(productRef, updateData);
      return { success: true };
    } catch (error) {
      logger.error('Error updating product:', error);
      throw error;
    }
  },

  /**
   * Delete product (admin only - soft delete)
   */
  async deleteProduct(productId, userId) {
    try {
      const productRef = doc(db, 'products', `product_${productId}`);
      await updateDoc(productRef, {
        is_active: false,
        updated_at: serverTimestamp(),
        updated_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting product:', error);
      throw error;
    }
  }
};

// ============================================
// ADDONS SERVICE
// ============================================
//
// Two coexisting kinds of add-on (ADR-0035 — FEAT-02.4):
//   - INDIVIDUAL: bound to one product via `product_id` (the original shipped
//     shape; every legacy doc is this kind — it has `product_id` and no
//     `scope`). "Remove" = soft-delete (is_active=false).
//   - SHARED: a catalog record (`scope:'shared'`, NO `product_id`) attached to
//     many products via the `linked_product_ids` array. Editing it propagates
//     to every linked product. "Remove from a product" = unlink (the doc
//     survives). `linked_product_ids.length` is the "used by N products" count.
//
// The shared kind was added ADDITIVELY — no legacy doc is rewritten, so there
// is no destructive migration. `getAddonsByProductId` returns the union of both
// kinds for a product. Requires a composite index on
// (linked_product_ids array-contains, is_active ==) — see firestore.indexes.json.

/**
 * Order a product's add-ons by the product's saved `addon_order` (an array of
 * addon_id). Add-ons present in the order array come first in that order; any
 * not listed (newly created, or a product with no saved order yet) fall to the
 * end, stably, by created-at then addon_id. Pure + exported for unit tests.
 *
 * @param {Array}  addons     merged add-on objects (each has `addon_id`)
 * @param {Array}  addonOrder ordered addon_id list (may be undefined/empty)
 * @returns {Array} a new, ordered array (does not mutate the input)
 */
export function orderAddons(addons, addonOrder) {
  const list = Array.isArray(addons) ? [...addons] : [];
  const order = Array.isArray(addonOrder) ? addonOrder : [];
  const rank = new Map(order.map((id, idx) => [id, idx]));
  const tieBreak = (a) => {
    const ts = a?.created_at?.seconds ?? a?.created_at ?? 0;
    return typeof ts === 'number' ? ts : 0;
  };
  return list.sort((a, b) => {
    const ra = rank.has(a.addon_id) ? rank.get(a.addon_id) : Infinity;
    const rb = rank.has(b.addon_id) ? rank.get(b.addon_id) : Infinity;
    if (ra !== rb) return ra - rb;
    const ta = tieBreak(a);
    const tb = tieBreak(b);
    if (ta !== tb) return ta - tb;
    return (a.addon_id || 0) - (b.addon_id || 0);
  });
}

export const addonsService = {
  /**
   * Get the add-ons attached to a product — the union of INDIVIDUAL add-ons
   * (product_id == productId) and SHARED add-ons (linked_product_ids contains
   * productId), de-duplicated and ordered by the product's `addon_order`.
   */
  async getAddonsByProductId(productId) {
    try {
      const pid = parseInt(productId);
      const addonsRef = collection(db, 'addons');

      const individualQ = query(
        addonsRef,
        where('product_id', '==', pid),
        where('is_active', '==', true)
      );
      const sharedQ = query(
        addonsRef,
        where('linked_product_ids', 'array-contains', pid),
        where('is_active', '==', true)
      );

      const [individualSnap, sharedSnap] = await Promise.all([
        getDocs(individualQ),
        getDocs(sharedQ),
      ]);

      const byId = new Map();
      [...individualSnap.docs, ...sharedSnap.docs].forEach((d) => {
        const data = d.data();
        // De-dupe defensively (a doc cannot satisfy both queries, but be safe).
        if (!byId.has(d.id)) {
          byId.set(d.id, { id: data.addon_id, firestoreId: d.id, ...data });
        }
      });
      const merged = Array.from(byId.values());

      // Sort by the product's saved order, when present.
      let addonOrder = [];
      try {
        const productSnap = await getDoc(doc(db, 'products', `product_${pid}`));
        if (productSnap.exists()) addonOrder = productSnap.data().addon_order || [];
      } catch (e) {
        // Ordering is best-effort; fall back to created-at order on failure.
        logger.error('Error reading addon_order for product:', e);
      }

      return orderAddons(merged, addonOrder);
    } catch (error) {
      logger.error('Error fetching addons:', error);
      throw error;
    }
  },

  /**
   * Get all active addons (both kinds).
   */
  async getAllAddons() {
    try {
      const addonsRef = collection(db, 'addons');
      const q = query(addonsRef, where('is_active', '==', true));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.data().addon_id,
        firestoreId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('Error fetching all addons:', error);
      throw error;
    }
  },

  /**
   * Get the SHARED add-on catalog — every active `scope:'shared'` add-on.
   * Powers the "Link existing add-on" picker. Two equality filters need no
   * composite index (Firestore serves them via single-field index merge).
   */
  async getSharedAddons() {
    try {
      const addonsRef = collection(db, 'addons');
      const q = query(
        addonsRef,
        where('scope', '==', 'shared'),
        where('is_active', '==', true)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.data().addon_id,
        firestoreId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('Error fetching shared addons:', error);
      throw error;
    }
  },

  // ========== ADMIN METHODS ==========

  /**
   * Create addon (admin only). Scope-aware:
   *   - scope:'shared'     → no product_id; linked_product_ids:[product_id]
   *     (the creating product is linked at birth).
   *   - scope:'individual' → product_id set (legacy/default behaviour).
   * A call with no `scope` but a `product_id` stays individual (back-compat).
   */
  async createAddon(addonData, userId) {
    try {
      const addonId = addonData.addon_id || Date.now();
      const addonRef = doc(db, 'addons', `addon_${addonId}`);
      const isShared = addonData.scope === 'shared';
      const newAddon = {
        addon_id: addonId,
        scope: isShared ? 'shared' : 'individual',
        name: addonData.name,
        description: addonData.description || '',
        price: addonData.price,
        image: addonData.image || '',
        image_url: addonData.image_url ?? addonData.image ?? null,
        is_active: addonData.is_active !== false,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userId,
        updated_by: userId,
        ...(isShared
          ? { linked_product_ids: addonData.product_id != null ? [parseInt(addonData.product_id)] : [] }
          : { product_id: addonData.product_id }),
      };

      await setDoc(addonRef, newAddon);
      return {
        success: true,
        addonId: addonId,
        firestoreId: `addon_${addonId}`
      };
    } catch (error) {
      logger.error('Error creating addon:', error);
      throw error;
    }
  },

  /**
   * Update addon (admin only). For a SHARED add-on this is the propagation
   * write — every linked product reflects it on next load.
   */
  async updateAddon(addonId, updates, userId) {
    try {
      const addonRef = doc(db, 'addons', `addon_${addonId}`);
      await updateDoc(addonRef, {
        ...updates,
        updated_at: serverTimestamp(),
        updated_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error updating addon:', error);
      throw error;
    }
  },

  /**
   * Link a SHARED add-on to a product (idempotent — arrayUnion). The canonical
   * doc's content is untouched; only its `linked_product_ids` grows.
   */
  async linkToProduct(addonId, productId, userId) {
    try {
      const addonRef = doc(db, 'addons', `addon_${addonId}`);
      await updateDoc(addonRef, {
        linked_product_ids: arrayUnion(parseInt(productId)),
        updated_at: serverTimestamp(),
        updated_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error linking addon to product:', error);
      throw error;
    }
  },

  /**
   * Unlink a SHARED add-on from a product (idempotent — arrayRemove). The
   * canonical add-on survives for every other linked product; NOT a delete.
   */
  async unlinkFromProduct(addonId, productId, userId) {
    try {
      const addonRef = doc(db, 'addons', `addon_${addonId}`);
      await updateDoc(addonRef, {
        linked_product_ids: arrayRemove(parseInt(productId)),
        updated_at: serverTimestamp(),
        updated_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error unlinking addon from product:', error);
      throw error;
    }
  },

  /**
   * Delete addon (admin only - soft delete). Used for INDIVIDUAL add-on remove
   * (a shared add-on is unlinked, not deleted, when removed from a product).
   */
  async deleteAddon(addonId, userId) {
    try {
      const addonRef = doc(db, 'addons', `addon_${addonId}`);
      await updateDoc(addonRef, {
        is_active: false,
        updated_at: serverTimestamp(),
        updated_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting addon:', error);
      throw error;
    }
  }
};

// ============================================
// PRODUCT CONTENT SERVICE
// ============================================
export const productContentService = {
  /**
   * Get content for a specific product
   */
  async getContentByProductId(productId) {
    try {
      const contentRef = doc(db, 'product_content', `content_${productId}`);
      const contentSnap = await getDoc(contentRef);

      if (contentSnap.exists()) {
        return {
          firestoreId: contentSnap.id,
          ...contentSnap.data()
        };
      }
      return null;
    } catch (error) {
      logger.error('Error fetching product content:', error);
      throw error;
    }
  },

  // ========== ADMIN METHODS ==========

  /**
   * Create or update product content (admin only)
   */
  async saveContent(productId, contentData, userId) {
    try {
      const contentRef = doc(db, 'product_content', `content_${productId}`);
      const contentSnap = await getDoc(contentRef);

      const data = {
        product_id: parseInt(productId),
        product_title: contentData.product_title,
        items: contentData.items || [],
        updated_at: serverTimestamp(),
        updated_by: userId
      };

      if (contentSnap.exists()) {
        await updateDoc(contentRef, data);
      } else {
        data.created_at = serverTimestamp();
        data.created_by = userId;
        await setDoc(contentRef, data);
      }
      return { success: true };
    } catch (error) {
      logger.error('Error saving product content:', error);
      throw error;
    }
  },

};

// ============================================
// PRODUCT DETAIL IMAGES SERVICE
// ============================================
export const productDetailImagesService = {
  /**
   * Get detail images for a specific product
   */
  async getImagesByProductId(productId) {
    try {
      const imagesRef = doc(db, 'product_detail_images', `images_${productId}`);
      const imagesSnap = await getDoc(imagesRef);

      if (imagesSnap.exists()) {
        return {
          firestoreId: imagesSnap.id,
          ...imagesSnap.data()
        };
      }
      return null;
    } catch (error) {
      logger.error('Error fetching product detail images:', error);
      throw error;
    }
  },

};

// ============================================
// CARTS SERVICE
// ============================================
export const cartsService = {
  /**
   * Get user's cart from Firestore
   */
  async getUserCart(userId) {
    try {
      const cartRef = doc(db, 'carts', userId);
      const cartSnap = await getDoc(cartRef);

      if (cartSnap.exists()) {
        return cartSnap.data().items || [];
      }
      return [];
    } catch (error) {
      logger.error('Error fetching user cart:', error);
      throw error;
    }
  },

  /**
   * Save/update user's cart to Firestore
   */
  async saveUserCart(userId, cartItems) {
    try {
      const cartRef = doc(db, 'carts', userId);
      const cartDoc = await getDoc(cartRef);

      const cartData = {
        user_id: userId,
        items: cartItems,
        last_modified: serverTimestamp()
      };

      if (cartDoc.exists()) {
        // Update existing cart
        await updateDoc(cartRef, cartData);
      } else {
        // Create new cart with userId as document ID
        await setDoc(cartRef, {
          ...cartData,
          created_at: serverTimestamp(),
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
        });
      }

      return { success: true };
    } catch (error) {
      logger.error('Error saving user cart:', error);
      throw error;
    }
  },

  /**
   * Merge guest cart with user cart on login
   */
  async mergeGuestCart(userId, guestCartItems) {
    try {
      if (!guestCartItems || guestCartItems.length === 0) {
        return { success: true, mergedCart: [] };
      }

      const userCart = await this.getUserCart(userId);
      const mergedCart = [...userCart];

      // Merge logic: add quantities for same products
      for (const guestItem of guestCartItems) {
        const existingIndex = mergedCart.findIndex(item => item.id === guestItem.id);

        if (existingIndex >= 0) {
          // Product exists, merge quantities and addons
          mergedCart[existingIndex].quantity += guestItem.quantity;

          // Merge addons (avoid duplicates)
          const existingAddons = mergedCart[existingIndex].addons || [];
          const newAddons = guestItem.addons || [];

          for (const addon of newAddons) {
            if (!existingAddons.some(a => a.id === addon.id)) {
              existingAddons.push(addon);
            }
          }
          mergedCart[existingIndex].addons = existingAddons;

          // Keep guest message if user item doesn't have one
          if (!mergedCart[existingIndex].message && guestItem.message) {
            mergedCart[existingIndex].message = guestItem.message;
          }
        } else {
          // New product, add to cart
          mergedCart.push(guestItem);
        }
      }

      await this.saveUserCart(userId, mergedCart);
      return { success: true, mergedCart };
    } catch (error) {
      logger.error('Error merging guest cart:', error);
      throw error;
    }
  },

  /**
   * Clear user's cart
   */
  async clearUserCart(userId) {
    try {
      const cartRef = doc(db, 'carts', userId);
      await updateDoc(cartRef, {
        items: [],
        last_modified: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      logger.error('Error clearing cart:', error);
      throw error;
    }
  }
};

// ============================================
// SITE SETTINGS SERVICE
// ============================================
export const siteSettingsService = {
  /**
   * Get TopBar settings
   */
  async getTopBarSettings() {
    try {
      const settingsRef = doc(db, 'site_settings', 'topbar');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        return settingsSnap.data();
      }
      return null;
    } catch (error) {
      logger.error('Error fetching TopBar settings:', error);
      throw error;
    }
  },

  /**
   * Get OfferBanner settings
   */
  async getOfferBannerSettings() {
    try {
      const settingsRef = doc(db, 'site_settings', 'offer_banner');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        return settingsSnap.data();
      }
      return null;
    } catch (error) {
      logger.error('Error fetching OfferBanner settings:', error);
      throw error;
    }
  },

  /**
   * Get all site settings
   */
  async getAllSettings() {
    try {
      const settingsRef = collection(db, 'site_settings');
      const querySnapshot = await getDocs(settingsRef);

      const settings = {};
      querySnapshot.docs.forEach(doc => {
        settings[doc.id] = doc.data();
      });

      return settings;
    } catch (error) {
      logger.error('Error fetching all site settings:', error);
      throw error;
    }
  },

  // ========== ADMIN METHODS ==========

  /**
   * Update TopBar settings (admin only)
   */
  async updateTopBarSettings(updates, userId) {
    try {
      const settingsRef = doc(db, 'site_settings', 'topbar');
      const updateData = {
        ...updates,
        updated_at: serverTimestamp()
      };

      await updateDoc(settingsRef, updateData);
      return { success: true };
    } catch (error) {
      logger.error('Error updating TopBar settings:', error);
      throw error;
    }
  },

  /**
   * Update OfferBanner settings (admin only)
   */
  async updateOfferBannerSettings(updates, userId) {
    try {
      const settingsRef = doc(db, 'site_settings', 'offer_banner');
      const updateData = {
        ...updates,
        updated_at: serverTimestamp()
      };

      await updateDoc(settingsRef, updateData);
      return { success: true };
    } catch (error) {
      logger.error('Error updating OfferBanner settings:', error);
      throw error;
    }
  },

  /**
   * Toggle TopBar visibility (admin only)
   */
  async toggleTopBar(isActive, userId) {
    try {
      return await this.updateTopBarSettings({ is_active: isActive }, userId);
    } catch (error) {
      logger.error('Error toggling TopBar:', error);
      throw error;
    }
  },

  /**
   * Toggle OfferBanner visibility (admin only)
   */
  async toggleOfferBanner(isActive, userId) {
    try {
      return await this.updateOfferBannerSettings({ is_active: isActive }, userId);
    } catch (error) {
      logger.error('Error toggling OfferBanner:', error);
      throw error;
    }
  },

  /**
   * Update category settings (admin only)
   */
  async updateCategorySettings(updates, userId) {
    try {
      const settingsRef = doc(db, 'site_settings', 'categories');
      const updateData = {
        ...updates,
        updated_at: serverTimestamp()
      };

      await setDoc(settingsRef, updateData, { merge: true });
      return { success: true };
    } catch (error) {
      logger.error('Error updating category settings:', error);
      throw error;
    }
  },

  /**
   * Update demo mode settings (admin only)
   */
  async updateDemoModeSettings(updates, userId) {
    try {
      const settingsRef = doc(db, 'site_settings', 'demo_mode');
      const updateData = {
        ...updates,
        updated_by: userId,
        updated_at: serverTimestamp()
      };

      await setDoc(settingsRef, updateData, { merge: true });
      return { success: true };
    } catch (error) {
      logger.error('Error updating demo mode settings:', error);
      throw error;
    }
  }
};

// ============================================
// NEWSLETTER SERVICE
// ============================================
export const newsletterService = {
  /**
   * Subscribe email to newsletter — POSTs to /api/newsletter/subscribe.
   * The server creates the doc with an HMAC unsubscribe_token attached
   * (FEAT-06.5 STORY-06.5.01), which we can't do client-side because the
   * HMAC secret has to stay server-only. Pre-existing client-direct creates
   * (legacy docs without a token) keep working — the unsubscribe endpoint
   * lazy-backfills the token on first verified unsubscribe.
   *
   * Returns the same shape callers were expecting:
   *   { success, message }
   * with an extra `alreadySubscribed` flag for callers that want it.
   */
  async subscribe(email) {
    try {
      // Lazy-load to avoid a circular import with services/api.js if
      // anything inside it ever reaches back into firestore.js.
      const { api } = await import('./api');
      const res = await api.post('/newsletter/subscribe', {
        email: String(email || '').trim().toLowerCase(),
        source: 'website_footer',
      });
      return {
        success: !!res?.success,
        alreadySubscribed: !!res?.alreadySubscribed,
        message: res?.message || 'Successfully subscribed!',
      };
    } catch (error) {
      logger.error('Error subscribing to newsletter:', error);
      throw error;
    }
  },

  // ========== ADMIN METHODS ==========

  /**
   * Get all subscribers (admin only)
   */
  async getAllSubscribers(activeOnly = true) {
    try {
      const subscribersRef = collection(db, 'newsletter_subscribers');
      let q;

      if (activeOnly) {
        q = query(subscribersRef, where('is_active', '==', true));
      } else {
        q = query(subscribersRef);
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('Error fetching newsletter subscribers:', error);
      throw error;
    }
  },

  /**
   * Filterable + cursor-paginated query for the admin Subscribers page
   * (EPIC-06 FEAT-06.1). Mirrors the shape of activityLogService.query and
   * ordersService.getOrdersPage.
   *
   * @param {Object} opts
   * @param {string} [opts.statusFilter]  — 'all' | 'active' | 'unsubscribed'
   *                                        Derived from is_active.
   * @param {string} [opts.sourceFilter]  — 'all' or a specific source string
   * @param {number} [opts.pageSize=50]
   * @param {Object} [opts.cursor]        — DocumentSnapshot from a prior page
   * @returns {Promise<{ subscribers, nextCursor, hasMore }>}
   *
   * Search-by-email is NOT server-side (no Firestore substring index). The
   * page does that client-side over the loaded results.
   */
  async getSubscribersPage({
    statusFilter = 'all',
    sourceFilter = 'all',
    pageSize = 50,
    cursor = null,
  } = {}) {
    try {
      const subscribersRef = collection(db, 'newsletter_subscribers');
      const constraints = [orderBy('subscribed_at', 'desc')];

      if (statusFilter === 'active') constraints.push(where('is_active', '==', true));
      else if (statusFilter === 'unsubscribed') constraints.push(where('is_active', '==', false));

      if (sourceFilter && sourceFilter !== 'all') {
        constraints.push(where('source', '==', sourceFilter));
      }

      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(fbLimit(pageSize + 1));

      const snap = await getDocs(query(subscribersRef, ...constraints));
      const docs = snap.docs;
      const hasMore = docs.length > pageSize;
      const visible = hasMore ? docs.slice(0, pageSize) : docs;

      return {
        subscribers: visible.map((d) => ({ id: d.id, ...d.data() })),
        nextCursor: hasMore ? visible[visible.length - 1] : null,
        hasMore,
      };
    } catch (error) {
      logger.error('Error querying subscribers page:', error);
      throw error;
    }
  },

  /**
   * Server-side count of active subscribers (EPIC-06 FEAT-06.2 STORY-06.2.02).
   * Uses Firestore's getCountFromServer aggregation so the compose page's
   * live audience figure doesn't download every subscriber doc on a 60s loop.
   * @returns {Promise<number>}
   */
  async getActiveSubscriberCount() {
    try {
      const subscribersRef = collection(db, 'newsletter_subscribers');
      const snap = await getCountFromServer(query(subscribersRef, where('is_active', '==', true)));
      return snap.data().count;
    } catch (error) {
      logger.error('Error counting active subscribers:', error);
      throw error;
    }
  },

};

// ============================================
// CAMPAIGNS SERVICE  (EPIC-06 FEAT-06.2 / 06.3)
// ----------------------------------------------------------------------------
// Canonical writer for newsletter campaign drafts and (later) sent campaigns.
// `subject + body_markdown` are the source of truth; `body_html` is derived
// on save and used by the send pipeline. The admin tab edits drafts here;
// the send pipeline (FEAT-06.3, blocked on Resend DNS) consumes them.
// ============================================
export const campaignsService = {
  /**
   * Create a new draft campaign. Returns the new campaignId.
   */
  async createCampaign(initial, userId) {
    try {
      const ref = collection(db, 'campaigns');
      const doc = {
        subject: initial.subject || '',
        body_markdown: initial.body_markdown || '',
        body_html: initial.body_html || '',
        status: 'draft',
        created_at: serverTimestamp(),
        created_by: userId || null,
        updated_at: serverTimestamp(),
        updated_by: userId || null,
      };
      const result = await addDoc(ref, doc);
      return { success: true, campaignId: result.id };
    } catch (error) {
      logger.error('Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Patch an existing campaign. Used by the auto-save loop in the editor;
   * the caller passes only the fields that changed.
   */
  async updateCampaign(campaignId, updates, userId) {
    try {
      const ref = doc(db, 'campaigns', campaignId);
      await updateDoc(ref, {
        ...updates,
        updated_at: serverTimestamp(),
        updated_by: userId || null,
      });
      return { success: true };
    } catch (error) {
      logger.error('Error updating campaign:', error);
      throw error;
    }
  },

  async getCampaignById(campaignId) {
    try {
      const ref = doc(db, 'campaigns', campaignId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      logger.error('Error fetching campaign:', error);
      throw error;
    }
  },

  /**
   * List campaigns newest-updated first. No pagination yet — campaigns are
   * low-volume (operator sends a few per month at most); when this grows
   * past ~100 we'll add cursor pagination matching the discounts pattern.
   */
  async getAllCampaigns() {
    try {
      const ref = collection(db, 'campaigns');
      const q = query(ref, orderBy('updated_at', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      logger.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  /**
   * Soft delete via `status: 'deleted'`. Real deletes never happen — sent
   * campaigns are audit evidence and operators don't get a hard-delete.
   * Drafts can be removed from the list this way too.
   */
  async deleteCampaign(campaignId, userId) {
    try {
      const ref = doc(db, 'campaigns', campaignId);
      await updateDoc(ref, {
        status: 'deleted',
        deleted_at: serverTimestamp(),
        deleted_by: userId || null,
        updated_at: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting campaign:', error);
      throw error;
    }
  },
};

// ============================================
// DISCOUNTS/COUPONS SERVICE
// ============================================
export const discountsService = {
  async validateDiscount(code) {
    try {
      const discountsRef = collection(db, 'discounts');
      const q = query(discountsRef, where('code', '==', code.toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return { valid: false, message: 'Invalid discount code' };
      }

      const discount = { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };

      if (!discount.is_active) {
        return { valid: false, message: 'This discount code is no longer active' };
      }

      if (discount.expires_at) {
        const expiryDate = discount.expires_at.toDate ? discount.expires_at.toDate() : new Date(discount.expires_at);
        if (expiryDate < new Date()) {
          return { valid: false, message: 'This discount code has expired' };
        }
      }

      if (discount.usage_limit && discount.usage_count >= discount.usage_limit) {
        return { valid: false, message: 'This discount code has reached its usage limit' };
      }

      return { valid: true, discount };
    } catch (error) {
      logger.error('Error validating discount:', error);
      throw error;
    }
  },

  async applyDiscount(discountId) {
    try {
      const discountRef = doc(db, 'discounts', discountId);
      const discountSnap = await getDoc(discountRef);

      if (discountSnap.exists()) {
        await updateDoc(discountRef, {
          usage_count: increment(1),
          last_used_at: serverTimestamp()
        });
      }
      return { success: true };
    } catch (error) {
      logger.error('Error applying discount:', error);
      throw error;
    }
  },

  async getAllDiscounts() {
    try {
      const discountsRef = collection(db, 'discounts');
      const querySnapshot = await getDocs(discountsRef);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching discounts:', error);
      throw error;
    }
  },

  async createDiscount(discountData, userId) {
    try {
      const discountsRef = collection(db, 'discounts');
      const newDiscount = {
        code: discountData.code.toUpperCase(),
        type: discountData.type,
        value: parseFloat(discountData.value),
        description: discountData.description || '',
        min_order_amount: discountData.min_order_amount || 0,
        // FEAT-04.2 fields the form collects — these were dropped on create
        // before BUG-20260527-01 (edit persisted them via {...updates}; create
        // had an explicit allow-list that pre-dated them). Default safely.
        min_item_count: discountData.min_item_count ?? null,
        valid_from: discountData.valid_from || null,
        applies_to: discountData.applies_to || 'all',
        applies_to_ids: Array.isArray(discountData.applies_to_ids) ? discountData.applies_to_ids : [],
        usage_limit: discountData.usage_limit || null,
        usage_count: 0,
        expires_at: discountData.expires_at || null,
        is_active: discountData.is_active !== false,
        created_at: serverTimestamp(),
        created_by: userId
      };

      const docRef = await addDoc(discountsRef, newDiscount);
      return { success: true, discountId: docRef.id };
    } catch (error) {
      logger.error('Error creating discount:', error);
      throw error;
    }
  },

  async updateDiscount(discountId, updates, userId) {
    try {
      const discountRef = doc(db, 'discounts', discountId);
      await updateDoc(discountRef, { ...updates, updated_at: serverTimestamp(), updated_by: userId });
      return { success: true };
    } catch (error) {
      logger.error('Error updating discount:', error);
      throw error;
    }
  },

  async deleteDiscount(discountId, userId) {
    try {
      const discountRef = doc(db, 'discounts', discountId);
      await updateDoc(discountRef, { is_active: false, deleted_at: serverTimestamp(), deleted_by: userId });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting discount:', error);
      throw error;
    }
  }
};

// ============================================
// SETTINGS SERVICE  (EPIC-03)
// ----------------------------------------------------------------------------
// Canonical singleton settings docs at `settings/{settingId}`.
// Known docs:
//   `store`          — public-readable; storefront Footer + admin profile tab
//   `notifications`  — admin-only; email recipient lists per event
// (`store` doc is publicly readable per firestore.rules; the rest are admin.)
//
// Reads return null when the doc doesn't exist yet — callers should treat
// that as "use defaults and create on first save".
// ============================================
export const settingsService = {
  async get(settingId) {
    try {
      const ref = doc(db, 'settings', settingId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      logger.error(`Error fetching settings/${settingId}:`, error);
      throw error;
    }
  },

  async update(settingId, payload, userId) {
    try {
      const ref = doc(db, 'settings', settingId);
      // `setDoc` with merge:true creates the doc if missing AND patches if it
      // already exists — settings/store doesn't need a separate "create" call.
      await setDoc(ref, {
        ...payload,
        updated_at: serverTimestamp(),
        updated_by: userId || null,
      }, { merge: true });
      return { success: true };
    } catch (error) {
      logger.error(`Error updating settings/${settingId}:`, error);
      throw error;
    }
  },
};

// ============================================
// REFUNDS SERVICE
// ============================================
export const refundsService = {
  async getRefundByOrderId(orderId) {
    try {
      const refundsRef = collection(db, 'refunds');
      const q = query(refundsRef, where('order_id', '==', orderId));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;
      return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
    } catch (error) {
      logger.error('Error fetching refund:', error);
      throw error;
    }
  },

  async getAllRefunds() {
    try {
      const refundsRef = collection(db, 'refunds');
      const q = query(refundsRef, orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching refunds:', error);
      throw error;
    }
  },

  async createRefund(refundData, userId) {
    try {
      const refundsRef = collection(db, 'refunds');
      const newRefund = {
        order_id: refundData.order_id,
        order_number: refundData.order_number,
        customer_email: refundData.customer_email,
        customer_name: refundData.customer_name,
        original_amount: refundData.original_amount,
        refund_amount: parseFloat(refundData.refund_amount),
        refund_type: refundData.refund_type,
        reason: refundData.reason,
        notes: refundData.notes || '',
        status: 'pending',
        created_at: serverTimestamp(),
        created_by: userId
      };

      const docRef = await addDoc(refundsRef, newRefund);

      if (refundData.order_id) {
        const orderRef = doc(db, 'orders', refundData.order_id);
        await updateDoc(orderRef, { refund_status: 'pending', refund_id: docRef.id, updated_at: serverTimestamp() });
      }

      return { success: true, refundId: docRef.id };
    } catch (error) {
      logger.error('Error creating refund:', error);
      throw error;
    }
  },

  async updateRefundStatus(refundId, status, userId, notes = '') {
    try {
      const refundRef = doc(db, 'refunds', refundId);
      const refundSnap = await getDoc(refundRef);
      if (!refundSnap.exists()) throw new Error('Refund not found');

      const refundData = refundSnap.data();
      const updateData = { status, updated_at: serverTimestamp(), updated_by: userId };
      if (status === 'completed') updateData.completed_at = serverTimestamp();
      if (notes) updateData.notes = notes;

      await updateDoc(refundRef, updateData);

      if (refundData.order_id) {
        const orderRef = doc(db, 'orders', refundData.order_id);
        await updateDoc(orderRef, { refund_status: status, updated_at: serverTimestamp() });
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating refund status:', error);
      throw error;
    }
  },

  async getRefundStats() {
    try {
      const refunds = await this.getAllRefunds();
      const stats = { total: refunds.length, pending: 0, approved: 0, completed: 0, rejected: 0, totalRefundedAmount: 0 };

      refunds.forEach(refund => {
        if (refund.status === 'pending') stats.pending++;
        else if (refund.status === 'approved') stats.approved++;
        else if (refund.status === 'completed') { stats.completed++; stats.totalRefundedAmount += refund.refund_amount || 0; }
        else if (refund.status === 'rejected') stats.rejected++;
      });

      return stats;
    } catch (error) {
      logger.error('Error fetching refund stats:', error);
      throw error;
    }
  }
};

// ============================================
// SHIPPING ZONES SERVICE
// ============================================
export const shippingService = {
  async getShippingRate(state) {
    try {
      const zonesRef = collection(db, 'shipping_zones');
      const querySnapshot = await getDocs(zonesRef);

      for (const zoneDoc of querySnapshot.docs) {
        const zone = zoneDoc.data();
        if (zone.states && zone.states.includes(state.toLowerCase()) && zone.is_active) {
          return { zone_id: zoneDoc.id, zone_name: zone.name, rate: zone.rate, estimated_days: zone.estimated_days };
        }
      }

      const defaultZone = querySnapshot.docs.find(d => d.data().is_default);
      if (defaultZone) {
        const zone = defaultZone.data();
        return { zone_id: defaultZone.id, zone_name: zone.name, rate: zone.rate, estimated_days: zone.estimated_days };
      }

      return { rate: 0, zone_name: 'Free Shipping' };
    } catch (error) {
      logger.error('Error fetching shipping rate:', error);
      throw error;
    }
  },

  async getAllZones() {
    try {
      const zonesRef = collection(db, 'shipping_zones');
      const querySnapshot = await getDocs(zonesRef);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching shipping zones:', error);
      throw error;
    }
  },

  async createZone(zoneData, userId) {
    try {
      const zonesRef = collection(db, 'shipping_zones');
      const newZone = {
        name: zoneData.name,
        states: (zoneData.states || []).map(s => s.toLowerCase()),
        rate: parseFloat(zoneData.rate),
        estimated_days: zoneData.estimated_days || '3-5 business days',
        is_active: zoneData.is_active !== false,
        is_default: zoneData.is_default || false,
        created_at: serverTimestamp(),
        created_by: userId
      };

      const docRef = await addDoc(zonesRef, newZone);
      return { success: true, zoneId: docRef.id };
    } catch (error) {
      logger.error('Error creating shipping zone:', error);
      throw error;
    }
  },

  async updateZone(zoneId, updates, userId) {
    try {
      const zoneRef = doc(db, 'shipping_zones', zoneId);
      const updateData = { ...updates, updated_at: serverTimestamp(), updated_by: userId };
      if (updates.states) updateData.states = updates.states.map(s => s.toLowerCase());
      await updateDoc(zoneRef, updateData);
      return { success: true };
    } catch (error) {
      logger.error('Error updating shipping zone:', error);
      throw error;
    }
  },

  async deleteZone(zoneId, userId) {
    try {
      const zoneRef = doc(db, 'shipping_zones', zoneId);
      await updateDoc(zoneRef, { is_active: false, deleted_at: serverTimestamp(), deleted_by: userId });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting shipping zone:', error);
      throw error;
    }
  }
};

// ============================================
// COURIERS SERVICE
// ============================================
// Admin-only. Storefront never reads this — tracking URLs are pre-computed
// at assignment time and stored on the order doc itself.
export const couriersService = {
  async getAllCouriers() {
    try {
      const couriersRef = collection(db, 'couriers');
      const querySnapshot = await getDocs(couriersRef);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching couriers:', error);
      throw error;
    }
  },

  async getCourierById(courierId) {
    try {
      const courierRef = doc(db, 'couriers', courierId);
      const snap = await getDoc(courierRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      logger.error('Error fetching courier:', error);
      throw error;
    }
  },

  async createCourier(courierData, userId) {
    try {
      const couriersRef = collection(db, 'couriers');
      const areas = (courierData.areas_served || []).map(a => String(a).toLowerCase());
      const newCourier = {
        name: String(courierData.name || '').trim(),
        contact_name: courierData.contact_name || '',
        contact_phone: courierData.contact_phone || '',
        contact_email: courierData.contact_email || '',
        service_type: courierData.service_type || 'nationwide',
        areas_served: areas.length ? areas : ['all'],
        base_rate: courierData.base_rate != null ? parseFloat(courierData.base_rate) : null,
        tracking_url_pattern: courierData.tracking_url_pattern || '',
        notes: courierData.notes || '',
        is_active: courierData.is_active !== false,
        created_at: serverTimestamp(),
        created_by: userId
      };
      const docRef = await addDoc(couriersRef, newCourier);
      return { success: true, courierId: docRef.id };
    } catch (error) {
      logger.error('Error creating courier:', error);
      throw error;
    }
  },

  async updateCourier(courierId, updates, userId) {
    try {
      const courierRef = doc(db, 'couriers', courierId);
      const updateData = { ...updates, updated_at: serverTimestamp(), updated_by: userId };
      if (updates.areas_served) {
        updateData.areas_served = updates.areas_served.map(a => String(a).toLowerCase());
      }
      if (updates.base_rate != null && updates.base_rate !== '') {
        updateData.base_rate = parseFloat(updates.base_rate);
      }
      if (updates.name) updateData.name = String(updates.name).trim();
      await updateDoc(courierRef, updateData);
      return { success: true };
    } catch (error) {
      logger.error('Error updating courier:', error);
      throw error;
    }
  },

  async deleteCourier(courierId, userId) {
    try {
      const courierRef = doc(db, 'couriers', courierId);
      await updateDoc(courierRef, {
        is_active: false,
        deleted_at: serverTimestamp(),
        deleted_by: userId
      });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting courier:', error);
      throw error;
    }
  }
};

// ============================================
// ACTIVITY LOG SERVICE
// ============================================
export const activityLogService = {
  async logActivity(activityData) {
    try {
      const logsRef = collection(db, 'activity_logs');
      const newLog = {
        action: activityData.action,
        entity_type: activityData.entity_type,
        entity_id: activityData.entity_id,
        entity_name: activityData.entity_name || '',
        details: activityData.details || {},
        user_id: activityData.user_id,
        user_email: activityData.user_email,
        created_at: serverTimestamp()
      };

      await addDoc(logsRef, newLog);
      return { success: true };
    } catch (error) {
      logger.error('Error logging activity:', error);
      return { success: false };
    }
  },

  async getRecentActivity(limitCount = 50) {
    try {
      const logsRef = collection(db, 'activity_logs');
      const q = query(logsRef, orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.slice(0, limitCount).map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching activity logs:', error);
      throw error;
    }
  },

  /**
   * Filterable + cursor-paginated query for the admin Activity Log page (EPIC-07).
   *
   * @param {Object} opts
   * @param {string} [opts.entityType]  — server-side equality filter
   * @param {string} [opts.actorId]     — server-side equality filter on user_id
   * @param {number} [opts.pageSize=50]
   * @param {Object} [opts.cursor]      — DocumentSnapshot from a previous page
   * @returns {Promise<{ logs, nextCursor, hasMore }>}
   *
   * Search-by-text is NOT server-side — Firestore has no substring index. The
   * page does that client-side over the loaded results (same ADR-0002 strategy
   * we use for the Orders page). For an actor list, the page reads `admins`.
   */
  async query({ entityType = null, actorId = null, pageSize = 50, cursor = null } = {}) {
    try {
      const logsRef = collection(db, 'activity_logs');
      const constraints = [orderBy('created_at', 'desc')];
      if (entityType) constraints.push(where('entity_type', '==', entityType));
      if (actorId) constraints.push(where('user_id', '==', actorId));
      if (cursor) constraints.push(startAfter(cursor));
      // Fetch one extra so we can tell if there's another page without
      // a count query.
      constraints.push(fbLimit(pageSize + 1));

      const snap = await getDocs(query(logsRef, ...constraints));
      const docs = snap.docs;
      const hasMore = docs.length > pageSize;
      const visible = hasMore ? docs.slice(0, pageSize) : docs;

      return {
        logs: visible.map((d) => ({ id: d.id, ...d.data() })),
        nextCursor: hasMore ? visible[visible.length - 1] : null,
        hasMore,
      };
    } catch (error) {
      logger.error('Error querying activity logs:', error);
      throw error;
    }
  },

  async getActivityByEntity(entityType, entityId) {
    try {
      const logsRef = collection(db, 'activity_logs');
      const q = query(
        logsRef,
        where('entity_type', '==', entityType),
        where('entity_id', '==', entityId),
        orderBy('created_at', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching entity activity:', error);
      throw error;
    }
  }
};

// ============================================
// ORDER NOTES SERVICE
// ============================================
export const orderNotesService = {
  async addNote(orderId, noteData, userId, userEmail) {
    try {
      const notesRef = collection(db, 'order_notes');
      const newNote = {
        order_id: orderId,
        note: noteData.note,
        is_internal: noteData.is_internal !== false,
        created_by: userId,
        created_by_email: userEmail,
        created_at: serverTimestamp()
      };

      const docRef = await addDoc(notesRef, newNote);
      return { success: true, noteId: docRef.id };
    } catch (error) {
      logger.error('Error adding order note:', error);
      throw error;
    }
  },

  async getNotesByOrderId(orderId) {
    try {
      const notesRef = collection(db, 'order_notes');
      const q = query(notesRef, where('order_id', '==', orderId), orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      logger.error('Error fetching order notes:', error);
      throw error;
    }
  },

  async deleteNote(noteId) {
    try {
      const noteRef = doc(db, 'order_notes', noteId);
      await updateDoc(noteRef, { deleted_at: serverTimestamp() });
      return { success: true };
    } catch (error) {
      logger.error('Error deleting note:', error);
      throw error;
    }
  }
};

